import { useIsRTL } from '../../../locale';
import { TreeProps, FlatTreeNode } from '../';
import { TreeNavigator } from './useTreeNavigation';

export enum TreeNodeNavigationKeys {
  ArrowUp = 'ArrowUp',
  ArrowDown = 'ArrowDown',
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  Home = 'Home',
  End = 'End',
  Enter = 'Enter',
  Space = ' ',
  Delete = 'Delete',
}

export type KeyboardAction = {
  keyPressed: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
};

export const useTreeNodeKeyboardHandler = (
  flatData: FlatTreeNode[],
  props: Pick<TreeProps, 'expandedNodes' | 'onExpand' | 'onSelect' | 'onDelete' | 'onActivate'>,
  navigator: TreeNavigator
) => {
  const isRTL = useIsRTL();

  return (rowIndex: number, eventData: KeyboardAction) => {
    const { node, parentId, siblingPosition, siblingCount } = flatData[rowIndex];
    const isExpanded = props.expandedNodes[node.id];

    let key = eventData.keyPressed;
    if (isRTL) {
      if (key === 'ArrowLeft') {
        key = 'ArrowRight';
      } else if (key === 'ArrowRight') {
        key = 'ArrowLeft';
      }
    }
    const findNodeIndex = (searchId: string) =>
      flatData.findIndex((node) => node.node.id === searchId);

    switch (key) {
      case TreeNodeNavigationKeys.ArrowUp:
        if (rowIndex > 0) {
          navigator.up();
        }
        break;

      case TreeNodeNavigationKeys.ArrowLeft:
        if (isExpanded) {
          props.onExpand(node, false);
        } else if (parentId) {
          navigator.up(rowIndex - findNodeIndex(parentId));
        }
        break;

      case TreeNodeNavigationKeys.ArrowDown:
        if (rowIndex < flatData.length - 1) {
          navigator.down();
        }
        break;

      case TreeNodeNavigationKeys.ArrowRight:
        if (isExpanded) {
          navigator.down();
        } else if (node.children && node.children.length > 0) {
          props.onExpand(node, true);
        }
        break;

      case TreeNodeNavigationKeys.Home:
        if (eventData.ctrlKey) {
          if (rowIndex > 0) {
            navigator.home();
          }
        } else {
          if (siblingPosition > 1 && parentId) {
            navigator.up(rowIndex - (findNodeIndex(parentId) + 1));
          }
        }
        break;

      case TreeNodeNavigationKeys.End:
        if (eventData.ctrlKey) {
          if (rowIndex < flatData.length - 1) {
            navigator.end();
          }
        } else {
          if (siblingPosition < siblingCount && parentId) {
            const parentNode = flatData[findNodeIndex(parentId)].node;
            const lastChild = parentNode.children![parentNode.children!.length - 1];
            const lastChildIndex = findNodeIndex(lastChild.id);
            navigator.down(lastChildIndex - rowIndex);
          }
        }
        break;

      case TreeNodeNavigationKeys.Enter:
        if (!node.disabled) {
          node.isActionable && props.onActivate?.(node);
        }
      case TreeNodeNavigationKeys.Space:
        if (!node.disabled) {
          props.onSelect(node, eventData);
        }
        break;
      case TreeNodeNavigationKeys.Delete:
        node.isDeletable && props.onDelete?.(node);
        break;
      default:
        return;
    }
  };
};
